import { Goal, Achievement, Career, Assessment, WeeklyProgress, AcademicPerformance, CareerPath, LeaderboardEntry, ChatMessage } from '@/types';
import { IUser, UserResponse } from '@/models/core/user';
import { IStudent, StudentResponse } from '@/models/core/student';
import { ISchool, SchoolResponse } from '@/models/core/school';
import { generateDemoSchools, demoSchoolResponses } from './demo-schools';

// Utility functions for demo data generation
const generateId = () => `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getRandomElement = <T>(array: readonly T[]): T => array[Math.floor(Math.random() * array.length)];

const getRandomDate = (start: Date, end: Date): Date => 
  new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));

// Sample data arrays
const FIRST_NAMES = ['John', 'Jane', 'David', 'Abike', 'Ide', 'Emily', 'Christopher', 'Mustafa', 'Daniel', 'Don'] as const;
const LAST_NAMES = ['Sulaimon', 'Jazzy', 'IbiRonke', 'Idris', 'Nnamdi', 'Garcia', 'chukwudi', 'Olabanji', 'Adamu', 'OluwaTosin'] as const;
const SCHOOL_NAMES = [
  'Greenwood High School', 
  'Riverside Academy', 
  'Sunrise Secondary School',
  'Pioneer College',
  'Excel Comprehensive School'
] as const;
const CAREER_INTERESTS = ['Medicine', 'Engineering', 'Computer Science', 'Business', 'Law', 'Education', 'Arts', 'Sports', 'Agriculture', 'Architecture'] as const;
const SUBJECTS = ['Mathematics', 'English', 'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Economics', 'Computer Studies', 'Art'] as const;
const CLASS_LEVELS = ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'] as const;

// Generate demo user (without password field)
export const generateDemoUser = (userType: 'student' | 'school_admin' = 'student'): UserResponse => {
  const firstName = getRandomElement(FIRST_NAMES);
  const lastName = getRandomElement(LAST_NAMES);
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;
  
  return {
    _id: generateId(),
    email,
    userType,
    status: 'active',
    emailVerified: true,
    loginAttempts: 0,
    twoFactorEnabled: false,
    
    name: `${firstName} ${lastName}`,
    avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${firstName}`,
    grade: userType === 'student' ? getRandomElement(['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12']) : '',
    role: userType === 'student' ? 'student' : 'school',
    joinDate: getRandomDate(new Date(2024, 0, 1), new Date()),
    totalPoints: Math.floor(Math.random() * 1000),
    tasksCompleted: Math.floor(Math.random() * 50),
    streakDays: Math.floor(Math.random() * 30),
    level: Math.floor(Math.random() * 10) + 1,
    rank: Math.floor(Math.random() * 100) + 1,
    
    preferences: {
      notifications: true,
      soundEffects: Math.random() > 0.5,
      theme: Math.random() > 0.5 ? 'light' : 'dark',
      language: 'en',
      timezone: 'Africa/Lagos',
      notificationSettings: {
        email: true,
        push: true,
        sms: false,
      },
    },
    
    metadata: {
      registrationSource: 'web',
      ipAddress: '192.168.1.1',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    },
    
    createdAt: getRandomDate(new Date(2024, 0, 1), new Date()),
    updatedAt: new Date(),
    isDeleted: false,
  };
};

// Generate demo student profile
export const generateDemoStudent = (userId: string, schoolId: string): StudentResponse => {
  const firstName = getRandomElement(FIRST_NAMES);
  const lastName = getRandomElement(LAST_NAMES);
  
  const classLevel = getRandomElement(CLASS_LEVELS);
  
  return {
    _id: generateId(),
    firstName,
    lastName,
    middleName: Math.random() > 0.7 ? getRandomElement(['James', 'Marie', 'Lee', 'Ann']) : undefined,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@student.example.com`,
    phone: Math.random() > 0.3 ? `+23480${Math.floor(Math.random() * 90000000) + 10000000}` : undefined,
    dateOfBirth: getRandomDate(new Date(2005, 0, 1), new Date(2010, 11, 31)),
    gender: Math.random() > 0.5 ? 'male' : 'female',
    
    studentId: `STU${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
    classLevel: classLevel,
    currentClass: `${classLevel}${String.fromCharCode(65 + Math.floor(Math.random() * 3))}`,
    admissionNumber: `ADM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    admissionDate: getRandomDate(new Date(2023, 0, 1), new Date(2024, 5, 30)),
    status: 'active',
    
    schoolId,
    userId,
    parentIds: [],
    
    address: {
      street: `${Math.floor(Math.random() * 1000) + 1} Main Street`,
      city: getRandomElement(['Lagos', 'Abuja', 'Port Harcourt', 'Kano', 'Ibadan']),
      state: getRandomElement(['Lagos', 'FCT', 'Rivers', 'Kano', 'Oyo']),
      country: 'Nigeria',
      postalCode: `${Math.floor(Math.random() * 900000) + 100000}`,
    },
    
    emergencyContact: {
      name: `${getRandomElement(FIRST_NAMES)} ${getRandomElement(LAST_NAMES)}`,
      relationship: getRandomElement(['Parent', 'Guardian', 'Sibling']),
      phone: `+23480${Math.floor(Math.random() * 90000000) + 10000000}`,
      email: `emergency${Math.floor(Math.random() * 1000)}@example.com`,
    },
    
    subjects: Array.from({ length: Math.floor(Math.random() * 5) + 3 }, (_, i) => SUBJECTS[i]),
    currentGPA: Number((Math.random() * 3 + 1).toFixed(2)),
    cumulativeGPA: Number((Math.random() * 3 + 1).toFixed(2)),
    attendanceRate: Number((Math.random() * 30 + 70).toFixed(1)),
    
    assessmentResults: [],
    careerInterests: [...new Set(Array.from({ length: 3 }, () => getRandomElement(CAREER_INTERESTS)))],
    careerMatches: Array.from({ length: 5 }, (_, i) => ({
      career: getRandomElement(CAREER_INTERESTS),
      matchPercentage: Math.floor(Math.random() * 40) + 60,
      confidence: Number((Math.random() * 0.3 + 0.7).toFixed(2)),
    })),
    
    learningPaths: [],
    goals: [],
    achievements: [],
    
    performanceMetrics: {
      academicScore: Math.floor(Math.random() * 40) + 60,
      engagementScore: Math.floor(Math.random() * 50) + 50,
      progressScore: Math.floor(Math.random() * 45) + 55,
      lastUpdated: new Date(),
    },
    
    engagementData: {
      loginFrequency: Math.floor(Math.random() * 10) + 5,
      sessionDuration: Math.floor(Math.random() * 120) + 30,
      assessmentCompletion: Math.floor(Math.random() * 40) + 60,
      resourceAccess: Math.floor(Math.random() * 20) + 10,
      lastActive: getRandomDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), new Date()),
    },
    
    preferences: {
      language: 'en',
      learningStyle: getRandomElement(['visual', 'auditory', 'kinesthetic', 'reading']),
      notifications: {
        email: true,
        sms: false,
        push: true,
      },
      privacy: {
        shareWithParents: true,
        shareWithTeachers: true,
        sharePerformanceData: true,
      },
    },
    
    healthInfo: Math.random() > 0.5 ? {
      bloodGroup: getRandomElement(['A+', 'B+', 'O+', 'AB+', 'A-', 'B-', 'O-', 'AB-']),
      allergies: Math.random() > 0.7 ? ['Peanuts', 'Dust'] : [],
      medications: Math.random() > 0.8 ? ['Vitamins'] : [],
      specialNeeds: [],
      medicalConditions: [],
    } : undefined,
    
    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${firstName}${lastName}`,
    documents: [],
    parentPortalAccess: {
      enabled: Math.random() > 0.3,
      parentEmails: [`parent${Math.floor(Math.random() * 1000)}@example.com`],
    },
    
    metadata: {
      onboardingCompleted: true,
      profileCompleteness: Math.floor(Math.random() * 40) + 60,
      lastAssessmentDate: getRandomDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()),
      nextAssessmentDue: getRandomDate(new Date(), new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)),
      riskFactors: Math.random() > 0.8 ? ['Attendance'] : [],
    },
    
    createdAt: getRandomDate(new Date(2023, 0, 1), new Date()),
    updatedAt: new Date(),
    isDeleted: false,
  };
};

// Generate demo goals with required properties
export const generateDemoGoals = (userId: string): Goal[] => {
  const goalTemplates = [
    { title: 'Complete Math Assignment', description: 'Finish all exercises in chapter 5', category: 'academic' as const, icon: '📚', color: '#3B82F6' },
    { title: 'Read 5 Books', description: 'Expand knowledge through reading', category: 'personal' as const, icon: '📖', color: '#10B981' },
    { title: 'Improve GPA', description: 'Achieve 3.5 GPA this semester', category: 'academic' as const, icon: '🎯', color: '#F59E0B' },
    { title: 'Learn New Skill', description: 'Complete online coding course', category: 'skill' as const, icon: '💻', color: '#8B5CF6' },
    { title: 'Participate in Sports', description: 'Join school basketball team', category: 'extracurricular' as const, icon: '🏀', color: '#EF4444' },
  ];

  return goalTemplates.map((template, index) => ({
    id: `goal_${userId}_${index}`,
    userId,
    title: template.title,
    description: template.description,
    category: template.category,
    targetValue: 100,
    currentValue: Math.floor(Math.random() * 100),
    progress: Math.floor(Math.random() * 100),
    deadline: getRandomDate(new Date(), new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)),
    targetDate: getRandomDate(new Date(), new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)),
    status: Math.random() > 0.7 ? 'completed' : Math.random() > 0.5 ? 'in-progress' : 'not-started',
    priority: getRandomElement(['low', 'medium', 'high'] as const),
    icon: template.icon,
    color: template.color,
    createdAt: getRandomDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()),
    updatedAt: new Date(),
  }));
};

// Generate demo achievements
export const generateDemoAchievements = (userId: string): Achievement[] => {
  const achievementTemplates = [
    { name: 'First Login', title: 'First Login', description: 'Successfully logged in for the first time', icon: '🎯', points: 10, category: 'engagement' as const },
    { name: 'Week Streak', title: 'Week Streak', description: 'Logged in for 7 consecutive days', icon: '🔥', points: 50, category: 'engagement' as const },
    { name: 'Assignment Master', title: 'Assignment Master', description: 'Completed 10 assignments', icon: '📚', points: 100, category: 'academic' as const },
    { name: 'Quiz Champion', title: 'Quiz Champion', description: 'Scored 100% on any quiz', icon: '🏆', points: 75, category: 'academic' as const },
    { name: 'Early Bird', title: 'Early Bird', description: 'Completed task before deadline', icon: '⏰', points: 25, category: 'milestone' as const },
  ];

  return achievementTemplates.map((template, index) => ({
    id: `ach_${userId}_${index}`,
    userId,
    name: template.name,
    title: template.title,
    description: template.description,
    icon: template.icon,
    points: template.points,
    unlocked: Math.random() > 0.3,
    unlockedAt: Math.random() > 0.3 ? getRandomDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()) : undefined,
    category: template.category,
    progress: Math.random() > 0.3 ? 100 : Math.floor(Math.random() * 100),
    createdAt: getRandomDate(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000), new Date()),
  }));
};

// Generate demo careers
export const generateDemoCareers = (): Career[] => {
  const careers = [
    {
      id: 'career_1',
      title: 'Software Engineer',
      description: 'Design, develop, and maintain software systems',
      category: 'Technology',
      salaryRange: { min: 60000, max: 150000, currency: 'USD' },
      educationRequired: "Bachelor's Degree",
      skills: ['Programming', 'Problem Solving', 'Teamwork'],
      demand: 'High' as const,
      growth: 'Much faster than average',
      matchScore: 85,
    },
    {
      id: 'career_2',
      title: 'Doctor',
      description: 'Diagnose and treat medical conditions',
      category: 'Healthcare',
      salaryRange: { min: 80000, max: 300000, currency: 'USD' },
      educationRequired: 'Medical Degree',
      skills: ['Empathy', 'Critical Thinking', 'Communication'],
      demand: 'High' as const,
      growth: 'Faster than average',
      matchScore: 78,
    },
    {
      id: 'career_3',
      title: 'Teacher',
      description: 'Educate students in various subjects',
      category: 'Education',
      salaryRange: { min: 40000, max: 80000, currency: 'USD' },
      educationRequired: "Bachelor's Degree",
      skills: ['Communication', 'Patience', 'Creativity'],
      demand: 'Medium' as const,
      growth: 'Average',
      matchScore: 65,
    },
  ];

  return careers;
};

// Generate demo career paths
export const generateDemoCareerPaths = (): CareerPath[] => {
  return [
    {
      id: 'path_1',
      title: 'Technology & Engineering',
      description: 'Careers in software development, IT, and engineering fields',
      matchPercentage: 85,
      skills: ['Programming', 'Problem Solving', 'Mathematics'],
      education: ["Bachelor's in Computer Science", "Bootcamps", "Certifications"]
    },
    {
      id: 'path_2',
      title: 'Healthcare & Medicine',
      description: 'Medical professionals and healthcare providers',
      matchPercentage: 78,
      skills: ['Empathy', 'Science', 'Communication'],
      education: ['Medical School', 'Nursing Degree', 'Health Sciences']
    }
  ];
};

// Generate demo leaderboard
export const generateDemoLeaderboard = (): LeaderboardEntry[] => {
  const names = ['Alice Johnson', 'Bob Smith', 'Carol Davis', 'David Wilson', 'Eva Brown', 'Frank Miller'];
  return names.map((name, index) => ({
    id: `leader_${index + 1}`,
    rank: index + 1,
    name,
    points: 1000 - (index * 150),
    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`,
    progress: Math.floor(Math.random() * 100),
  }));
};

// Generate demo chat messages
export const generateDemoChatMessages = (): ChatMessage[] => {
  return [
    {
      id: 'msg_1',
      sender: 'Career Advisor',
      message: 'Hello! I noticed you scored high in analytical thinking. Have you considered careers in technology?',
      timestamp: new Date(Date.now() - 3600000),
      type: 'received',
    },
    {
      id: 'msg_2',
      sender: 'You',
      message: 'That sounds interesting! What specific tech careers would you recommend?',
      timestamp: new Date(Date.now() - 1800000),
      type: 'sent',
    },
    {
      id: 'msg_3',
      sender: 'Career Advisor',
      message: 'Based on your assessment, Software Engineering, Data Science, and Cybersecurity would be great fits!',
      timestamp: new Date(Date.now() - 600000),
      type: 'received',
    },
  ];
};

// Main demo data generator
export const generateDemoData = () => {
  const demoUser = generateDemoUser('student');
  const demoStudent = generateDemoStudent(demoUser._id as string, 'demo_school_1');
  const goals = generateDemoGoals(demoUser._id as string);
  const achievements = generateDemoAchievements(demoUser._id as string);
  const careers = generateDemoCareers();
  const careerPaths = generateDemoCareerPaths();
  const leaderboard = generateDemoLeaderboard();
  const chatMessages = generateDemoChatMessages();

  return {
    user: demoUser,
    student: demoStudent,
    goals,
    achievements,
    careers,
    careerPaths,
    leaderboard,
    chatMessages,
    
    // Additional demo data
    assessments: [
      {
        id: 'assess_1',
        title: 'Career Interest Assessment',
        description: 'Discover your career interests and preferences',
        completed: true,
        score: 85,
        maxScore: 100,
        takenAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      },
    ],
    
    weeklyProgress: [
      { week: 'Week 1', progress: 65 },
      { week: 'Week 2', progress: 78 },
      { week: 'Week 3', progress: 82 },
      { week: 'Week 4', progress: 75 },
    ],
    
    academicPerformance: [
      { subject: 'Mathematics', score: 85 },
      { subject: 'English', score: 78 },
      { subject: 'Physics', score: 82 },
      { subject: 'Chemistry', score: 75 },
    ],
  };
};

// Generate demo students
export const demoStudents: IStudent[] = Array.from({ length: 5 }, (_, index) => 
  generateDemoStudent(`demo_user_${index}`, `demo_school_${Math.floor(index / 2) + 1}`)
);

// Your existing demo user data
export const demoUser: UserResponse = generateDemoUser('student');

// Your existing demo data
export const demoGoals = generateDemoGoals(demoUser._id as string);
export const demoAchievements = generateDemoAchievements(demoUser._id as string);
export const demoCareers = generateDemoCareers();
export const demoAssessments = [
  {
    id: 'assess_1',
    title: 'Career Interest Assessment',
    description: 'Discover your career interests and preferences',
    completed: true,
    score: 85,
    maxScore: 100,
    takenAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  },
];
export const demoWeeklyProgress = [
  { week: 'Week 1', progress: 65 },
  { week: 'Week 2', progress: 78 },
  { week: 'Week 3', progress: 82 },
  { week: 'Week 4', progress: 75 },
];
export const demoAcademicPerformance = [
  { subject: 'Mathematics', score: 85 },
  { subject: 'English', score: 78 },
  { subject: 'Physics', score: 82 },
  { subject: 'Chemistry', score: 75 },
];
export const demoCareerPaths = generateDemoCareerPaths();
export const demoLeaderboard = generateDemoLeaderboard();
export const demoChatMessages = generateDemoChatMessages();

// Generate demo schools with relationships
export const demoSchools: ISchool[] = generateDemoSchools([demoUser], demoStudents);

// Export everything as a single object for easy importing
export const demoData = {
  user: demoUser,
  goals: demoGoals,
  achievements: demoAchievements,
  careers: demoCareers,
  assessments: demoAssessments,
  weeklyProgress: demoWeeklyProgress,
  academicPerformance: demoAcademicPerformance,
  careerPaths: demoCareerPaths,
  leaderboard: demoLeaderboard,
  chatMessages: demoChatMessages,
  students: demoStudents,
  schools: demoSchools,
  schoolResponses: demoSchoolResponses
};

// Default export with sample data
export default demoData;